import { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw, Camera, FileText, User, Newspaper, Database, HardDrive } from 'lucide-react';
import { articleService, editionService, authorService, storageService } from '../services/api';
import styles from './BibliotecaMultimediaPage.module.css';

const TIPOS = [
  { id: 'todos',     label: 'Todo',               icon: null },
  { id: 'articulos', label: 'Imágenes artículos',  icon: Camera },
  { id: 'autores',   label: 'Fotos de autores',    icon: User },
  { id: 'ediciones', label: 'PDFs y portadas',     icon: FileText },
  { id: 'periodico', label: 'Logo / Isotipo',      icon: Newspaper },
];

const CARPETAS_BUCKET = [
  { prefijo: '',           label: 'Todo el bucket' },
  { prefijo: 'articulos/', label: 'Artículos' },
  { prefijo: 'autores/',   label: 'Autores' },
  { prefijo: 'ediciones/', label: 'Ediciones' },
  { prefijo: 'periodicos/',label: 'Periódico' },
  { prefijo: 'publicidad/',label: 'Publicidad' },
  { prefijo: 'general/',   label: 'General' },
];

const MIMES_IMAGEN = ['image/jpeg','image/png','image/gif','image/webp','image/svg+xml'];
const MIMES_VIDEO  = ['video/mp4','video/webm','video/ogg'];
const MIMES_AUDIO  = ['audio/mpeg','audio/mp3','audio/aac','audio/ogg'];

export default function BibliotecaMultimediaPage() {
  const [modo,     setModo]     = useState('bd');    // 'bd' | 'bucket'
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [tipo,     setTipo]     = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [carpeta,  setCarpeta]  = useState('');

  // ── MODO BD ──────────────────────────────────────────────────
  const loadBD = useCallback(async () => {
    setLoading(true);
    try {
      const [articulos, autores, ediciones] = await Promise.all([
        articleService.getAll ? articleService.getAll({ limit: 200 }) : [],
        authorService.getAll  ? authorService.getAll()                : [],
        editionService.getAll ? editionService.getAll({ limit: 100 }) : [],
      ]);
      const resultado = [];
      (articulos.articles || articulos || []).forEach(a => {
        if (a.image || a.imageUrl) resultado.push({
          id: `art-${a.id}`, tipo: 'articulos',
          nombre: a.title || a.titulo || 'Artículo sin título',
          url: a.image || a.imageUrl, formato: 'imagen',
          ref: `Artículo #${a.id}`, fecha: a.publishedAt || a.createdAt, slug: a.slug,
        });
      });
      (autores.authors || autores || []).forEach(a => {
        if (a.avatar || a.avatarUrl) resultado.push({
          id: `aut-${a.id}`, tipo: 'autores',
          nombre: a.name || a.nombre || 'Autor',
          url: a.avatar || a.avatarUrl, formato: 'imagen',
          ref: `Autor: ${a.name || a.nombre}`, fecha: null,
        });
      });
      (ediciones.editions || ediciones || []).forEach(e => {
        if (e.pdfUrl) resultado.push({ id: `edi-pdf-${e.id}`, tipo: 'ediciones', nombre: `Edición Nº ${e.number || e.numero} — PDF`, url: e.pdfUrl, formato: 'pdf', ref: `Edición #${e.id}`, fecha: e.publishedAt });
        if (e.coverImage || e.coverUrl) resultado.push({ id: `edi-cov-${e.id}`, tipo: 'ediciones', nombre: `Edición Nº ${e.number || e.numero} — Portada`, url: e.coverImage || e.coverUrl, formato: 'imagen', ref: `Edición #${e.id}`, fecha: e.publishedAt });
      });
      setItems(resultado);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, []);

  // ── MODO BUCKET ───────────────────────────────────────────────
  const loadBucket = useCallback(async () => {
    setLoading(true);
    try {
      const data = await storageService.listarBucket(carpeta);
      const resultado = (data || []).map(obj => {
        let formato = 'otro';
        if (MIMES_IMAGEN.includes(obj.contentType)) formato = 'imagen';
        else if (MIMES_VIDEO.includes(obj.contentType)) formato = 'video';
        else if (MIMES_AUDIO.includes(obj.contentType)) formato = 'audio';
        else if (obj.contentType === 'application/pdf') formato = 'pdf';
        const partes = obj.objectName.split('/');
        return {
          id:     obj.objectName,
          tipo:   partes[0] || 'general',
          nombre: partes[partes.length - 1],
          url:    obj.urlPublica,
          gsUri:  obj.gsUri,
          formato,
          ref:    obj.objectName,
          fecha:  obj.actualizadoEn,
          tamano: obj.tamanioBytes,
        };
      });
      setItems(resultado);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [carpeta]);

  useEffect(() => {
    setItems([]); setTipo('todos'); setBusqueda('');
    if (modo === 'bd') loadBD(); else loadBucket();
  }, [modo, loadBD, loadBucket]);

  const filtrados = items.filter(it => {
    if (modo === 'bd' && tipo !== 'todos' && it.tipo !== tipo) return false;
    if (busqueda && !it.nombre.toLowerCase().includes(busqueda.toLowerCase()) &&
        !it.ref.toLowerCase().includes(busqueda.toLowerCase())) return false;
    return true;
  });

  const conteo = (t) => t === 'todos' ? items.length : items.filter(i => i.tipo === t).length;

  const formatBytes = (b) => {
    if (!b) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024*1024) return `${(b/1024).toFixed(1)} KB`;
    return `${(b/1024/1024).toFixed(1)} MB`;
  };

  const renderPreview = (it) => {
    if (it.formato === 'imagen') return (
      <img src={it.url} alt={it.nombre}
        style={{ width:'100%', height:120, objectFit:'cover', borderRadius:'6px 6px 0 0', background:'#f0f0f0', display:'block' }}
        onError={e => { e.target.style.display='none'; }} />
    );
    if (it.formato === 'video') return (
      <video src={it.url} muted style={{ width:'100%', height:120, objectFit:'cover', borderRadius:'6px 6px 0 0', background:'#111' }} />
    );
    const iconos = { audio:'🎵', html:'🌐', pdf:'📄', otro:'📎' };
    return (
      <div style={{ width:'100%', height:120, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'#f5f5f5', borderRadius:'6px 6px 0 0', fontSize:32 }}>
        {iconos[it.formato] || '📎'}
        <span style={{ fontSize:10, color:'#888', marginTop:4 }}>{it.formato?.toUpperCase()}</span>
      </div>
    );
  };

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Biblioteca Multimedia</h1>
          <p className={styles.subtitle}>Material gráfico editorial del periódico</p>
        </div>
        <button className={styles.btnRefresh} onClick={() => modo === 'bd' ? loadBD() : loadBucket()} disabled={loading}>
          <RefreshCw size={14} className={loading ? styles.spin : ''} />
          Actualizar
        </button>
      </div>

      {/* Selector de modo */}
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        <button onClick={() => setModo('bd')} className={`${styles.modoBtn} ${modo === 'bd' ? styles.modoBtnActive : ''}`}>
          <Database size={14} />
          Desde la base de datos
          <span style={{ fontSize:11, color: modo === 'bd' ? 'rgba(255,255,255,0.7)' : '#aaa' }}>
            — referencias registradas
          </span>
        </button>
        <button onClick={() => setModo('bucket')} className={`${styles.modoBtn} ${modo === 'bucket' ? styles.modoBtnActive : ''}`}>
          <HardDrive size={14} />
          Desde el repositorio físico
          <span style={{ fontSize:11, color: modo === 'bucket' ? 'rgba(255,255,255,0.7)' : '#aaa' }}>
            — archivos reales en Storage
          </span>
        </button>
      </div>

      {/* Filtros BD */}
      {modo === 'bd' && (
        <div className={styles.tipoBar}>
          {TIPOS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setTipo(id)}
              className={`${styles.tipoBtn} ${tipo === id ? styles.tipoBtnActive : ''}`}>
              {Icon && <Icon size={14} />}
              {label}
              <span className={styles.conteo}>{conteo(id)}</span>
            </button>
          ))}
        </div>
      )}

      {/* Filtros Bucket */}
      {modo === 'bucket' && (
        <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
          <span style={{ fontSize:12, color:'#888', fontWeight:600 }}>Carpeta:</span>
          {CARPETAS_BUCKET.map(c => (
            <button key={c.prefijo} onClick={() => { setCarpeta(c.prefijo); }}
              className={`${styles.tipoBtn} ${carpeta === c.prefijo ? styles.tipoBtnActive : ''}`}
              style={{ fontSize:12 }}>
              {c.label}
            </button>
          ))}
        </div>
      )}

      {/* Búsqueda */}
      <div className={styles.searchBar}>
        <Search size={14} className={styles.searchIcon} />
        <input placeholder='Buscar por nombre o ruta...' value={busqueda}
          onChange={e => setBusqueda(e.target.value)} className={styles.searchInput} />
        <span style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', fontSize:12, color:'#aaa' }}>
          {filtrados.length} archivo{filtrados.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Grid */}
      {loading ? (
        <div className={styles.empty}>Cargando...</div>
      ) : filtrados.length === 0 ? (
        <div className={styles.empty}>No hay archivos con esos filtros.</div>
      ) : (
        <div className={styles.grid}>
          {filtrados.map(it => (
            <div key={it.id} className={styles.card}>
              <div className={styles.preview} onClick={() => window.open(it.url, '_blank', 'noopener')} style={{ cursor:'pointer' }}>
                {renderPreview(it)}
              </div>
              <div className={styles.cardInfo}>
                <div className={styles.cardNombre} title={it.nombre}>{it.nombre}</div>
                <div className={styles.cardRef}>{it.ref}</div>
                {it.fecha && <div className={styles.cardFecha}>{new Date(it.fecha).toLocaleDateString('es-CO', { day:'2-digit', month:'short', year:'numeric' })}</div>}
                {it.tamano > 0 && <div className={styles.cardFecha}>{formatBytes(it.tamano)}</div>}
              </div>
              <div className={styles.cardActions}>
                <a href={it.url} target='_blank' rel='noreferrer' className={styles.btnVer}>Ver</a>
                <button className={styles.btnCopiar} onClick={() => navigator.clipboard.writeText(it.url)} title='Copiar URL'>Copiar URL</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
